/**
 * The idea library (make-up-your-own spec §3.1, plan Task 1): static story ideas for "Make up your own story" in
 * both create flows and on the site.
 *
 * CLIENT-SAFE: imports only `@/lib/catalogue/links`, `@/lib/picture-book/central-roles` (import-free) and types
 * (idea-cards.test.ts walks the imports; a server-only module in a client bundle crashed /create-book on 2026-10-29).
 *
 * Copy rules: no pets (owner parked them); no title collides with a catalogue story or comic sample; `{names}` appears
 * only where one name and several read the same (an object, before "must"/"can", or in the past tense).
 */
import { createBookHref, createComicHref } from '@/lib/catalogue/links';
import { isGrandparentOfCast } from '@/lib/picture-book/central-roles';
import type { MangaSubStyle } from '@/types/manga';
import type { ReaderBand } from '@/utils/manga/comic-story-profile';

export type IdeaProduct = 'picture-book' | 'comic';
export type IdeaOccasion = 'birthday' | 'christmas' | 'school';
export type IdeaNeed = 'grandparent' | 'baby';
export type IdeaIcon = 'moon' | 'flashlight' | 'droplets' | 'cloud' | 'party-popper' | 'snowflake' | 'rocket'
  | 'ferris-wheel' | 'orbit' | 'map' | 'bug' | 'train-front' | 'baby' | 'bus' | 'repeat' | 'gift' | 'tent' | 'zap'
  | 'swords' | 'cake' | 'chef-hat' | 'cloud-snow' | 'clock' | 'trees' | 'sparkles' | 'castle';

export interface IdeaCard {
  id: string;
  product: IdeaProduct;
  chip: string;
  title: string;
  idea: string;
  bands: readonly ReaderBand[];
  icon: IdeaIcon;
  styles?: readonly MangaSubStyle[];
  occasion?: IdeaOccasion;
  occasionOnly?: boolean;
  needs?: IdeaNeed;
}

export interface IdeaCastPerson { name: string; age?: string | number | null; relationship?: string | null; isMainCharacter?: boolean }
export interface FilledIdea { id: string; title: string; idea: string; icon: IdeaIcon; chip: string }
export interface IdeaPick { id: string; title: string; idea: string }

export const SURPRISE_IDEA = 'surprise';
export const IDEAS_SHOWN = 6;
export const IDEAS_MAX = 12;
/** The story-title input's maxLength. */
const TITLE_MAX = 80;

const PB = 'picture-book' as const;
const C = 'comic' as const;
const EMOWG: readonly ReaderBand[] = ['E', 'M', 'O', 'W', 'G'];

export const IDEA_CARDS: readonly IdeaCard[] = [
  // ── Picture books ──
  { id: 'pyjama-parade', product: PB, chip: 'The pyjama parade', title: "{hero}'s Pyjama Parade",
    idea: 'At bedtime every toy in the house joins {names} for a pyjama parade, one by one, until the whole house is fast asleep.',
    bands: ['T', 'E'], icon: 'moon' },
  { id: 'teddy-rescue', product: PB, chip: 'Teddy rescue mission', title: 'The Teddy Rescue Mission',
    idea: "{hero}'s favourite teddy is left behind at the park, and it is up to {names} to bring it home by torchlight before morning.",
    bands: ['T', 'E'], icon: 'flashlight' },
  { id: 'puddle-town', product: PB, chip: 'The upside-down puddle', title: 'The Puddle That Went Somewhere',
    idea: '{hero} jumps in a puddle and splashes down in an upside-down town where fish ride bicycles.',
    bands: ['T', 'E', 'M'], icon: 'droplets' },
  { id: 'sofa-fort', product: PB, chip: 'A fort in the clouds', title: 'The Sofa Fort in the Clouds',
    idea: 'On a rainy afternoon a blanket fort built by {names} floats up and up, all the way to a castle in the clouds.',
    bands: ['T', 'E', 'M'], icon: 'cloud' },
  { id: 'birthday-balloon', product: PB, chip: 'The runaway balloon', title: 'The Birthday Balloon That Got Away',
    idea: "{hero}'s birthday balloon drifts away, and the chase takes {names} over rooftops, across the river and right through the middle of the fair.",
    bands: ['T', 'E', 'M'], icon: 'party-popper', occasion: 'birthday' },
  { id: 'snow-castle', product: PB, chip: 'A snow castle wakes up', title: 'The Snow Castle That Woke Up',
    idea: 'At midnight the doors of the snow castle built by {names} swing open onto a kingdom of snow folk who need their help.',
    bands: ['E', 'M', 'O'], icon: 'snowflake' },
  { id: 'garden-rocket', product: PB, chip: 'A rocket in the garden', title: 'The Rocket in the Back Garden',
    idea: 'A small rocket lands by the washing line, and its tiny pilot needs {names} to help find the way home.',
    bands: ['E', 'M', 'O'], icon: 'rocket' },
  { id: 'street-fair', product: PB, chip: 'The midnight fair', title: 'The Night the Fair Came to Our Street',
    idea: 'A fair pops up outside overnight, and its Ferris wheel lifts {names} all the way up to the stars.',
    bands: ['E', 'M', 'O'], icon: 'ferris-wheel' },
  { id: 'comet-race', product: PB, chip: 'Racing a comet', title: 'The Great Comet Race',
    idea: 'A passing comet gives {names} a ride, and the race around the sun for the Great Space Cup is on.',
    bands: EMOWG, icon: 'orbit' },
  { id: 'suitcase-map', product: PB, chip: 'A map in a suitcase', title: 'The Map in the Old Suitcase',
    idea: 'A map falls out of a dusty old suitcase and leads {names} to a treasure nobody expected.',
    bands: EMOWG, icon: 'map' },
  { id: 'ant-size', product: PB, chip: 'Shrunk to ant size', title: '{hero} Shrinks to Ant Size',
    idea: 'A sneeze turns {hero} tiny, and the garden becomes a jungle with beetle taxis and a dew-drop lake.',
    bands: ['E', 'M', 'O', 'W'], icon: 'bug' },
  { id: 'underwater-train', product: PB, chip: 'The underwater train', title: 'The Underwater Train',
    idea: 'A train that runs along the seabed carries {names} off to deliver a letter to a lonely whale.',
    bands: EMOWG, icon: 'train-front' },
  { id: 'newest-crew', product: PB, chip: 'The newest crew member', title: '{hero} and the Newest Crew Member',
    idea: 'A tiny new crew member has joined the family ship, and Captain {hero} shows the baby every secret corner of home.',
    bands: ['T', 'E', 'M', 'O'], icon: 'baby', needs: 'baby' },
  { id: 'school-shortcut', product: PB, chip: 'The school bus shortcut', title: 'The School Bus That Took a Shortcut',
    idea: 'On the first morning of term the school bus takes a shortcut that carries {names} through the jungle, past the moon and under the sea, and still gets to school on time.',
    bands: ['E', 'M', 'O', 'W'], icon: 'bus', occasion: 'school', occasionOnly: true },
  { id: 'elder-swap', product: PB, chip: 'Swapping jobs for a day', title: 'The Day We Swapped Jobs with {elder}',
    idea: "For one whole day {hero} does {elder}'s jobs and {elder} does {hero}'s, and nobody expected it to be this much fun.",
    bands: EMOWG, icon: 'repeat', needs: 'grandparent' },
  { id: 'reindeer-lost', product: PB, chip: 'The lost reindeer', title: 'The Night the Reindeer Got Lost',
    idea: 'On Christmas Eve a lost young reindeer lands in the garden, and it is up to {names} to guide it back to the sleigh by midnight.',
    bands: ['T', 'E', 'M', 'O'], icon: 'snowflake', occasion: 'christmas', occasionOnly: true },
  { id: 'new-house-christmas', product: PB, chip: 'First Christmas in a new house', title: 'Our First Christmas in the New House',
    idea: 'The decorations were lost in the move, so {names} made every one by hand, and on Christmas morning the new house finally felt like home.',
    bands: EMOWG, icon: 'gift', occasion: 'christmas', occasionOnly: true },

  // ── Comics ──
  { id: 'superpowers', product: C, chip: 'Superpowers on a school day', title: '{hero} Wakes Up with Superpowers',
    idea: 'Super-speed on a school day, and nobody can find out, until a runaway ice-cream van needs stopping.',
    bands: EMOWG, styles: ['superhero'], icon: 'zap' },
  { id: 'moonbase-rescue', product: C, chip: 'Moonbase rescue', title: 'Mission: Moonbase Rescue',
    idea: "The moonbase's greenhouse is failing, and only the rocket flown by {names} can reach it in time.",
    bands: EMOWG, styles: ['superhero', 'european'], icon: 'rocket' },
  { id: 'volcano-treasure', product: C, chip: 'The volcano treasure race', title: 'Race to the Volcano Treasure',
    idea: 'A rival crew of kid pirates is racing {names} across a smoking island to the treasure at its peak.',
    bands: EMOWG, styles: ['european', 'shonen'], icon: 'map' },
  { id: 'night-market-ninjas', product: C, chip: 'Night-market ninjas', title: 'Night-Market Ninjas',
    idea: "A sneaky thief swipes the lantern-maker's prize lantern, and {names} must chase it across the rooftops of the glowing night market.",
    bands: EMOWG, styles: ['shonen'], icon: 'swords' },
  { id: 'bouncy-castle', product: C, chip: 'The runaway bouncy castle', title: 'The Runaway Bouncy Castle',
    idea: "The party's bouncy castle breaks free and bounces off down the high street, and {names} must catch it before it reaches the sea.",
    bands: ['T', 'E', 'M', 'O'], styles: ['chibi', 'cartoon'], icon: 'castle', occasion: 'birthday' },
  { id: 'robot-chef', product: C, chip: 'Robot chef gone rogue', title: 'Robot Chef Gone Rogue',
    idea: "The family's new kitchen robot makes jelly the size of a trampoline and won't stop cooking until {names} can switch it off.",
    bands: ['T', 'E', 'M', 'O', 'W'], styles: ['chibi', 'cartoon'], icon: 'chef-hat' },
  { id: 'weather-machine', product: C, chip: 'The weather machine', title: 'Team {hero} vs the Weather Machine',
    idea: 'Someone is making it snow in summer, and {names} must track the weather machine to its secret lair.',
    bands: EMOWG, styles: ['superhero'], icon: 'cloud-snow' },
  { id: 'alien-classmate', product: C, chip: 'The alien in our class', title: 'The Alien in Our Class',
    idea: 'A new classmate is secretly from another planet, and {hero} has one week to keep the secret and win Sports Day.',
    bands: EMOWG, styles: ['cartoon'], icon: 'sparkles', occasion: 'school' },
  { id: 'lunchbox-time-machine', product: C, chip: 'The lunchbox time machine', title: 'The Lunchbox Time Machine',
    idea: "Opening {hero}'s lunchbox sends {names} back to the age of the dinosaurs, and lunch break is only 40 minutes long.",
    bands: EMOWG, styles: ['cartoon', 'european'], icon: 'clock' },
  { id: 'big-match', product: C, chip: 'The big match', title: 'The Big Match',
    idea: "Football, cricket or basketball: the final is tomorrow, the star player is injured, and it's {hero}'s turn to step up.",
    bands: EMOWG, styles: ['shonen'], icon: 'trees' },
  { id: 'clocktower', product: C, chip: 'The secret of the clocktower', title: 'The Secret of the Clocktower',
    idea: 'The town clock stops at midnight, and {names} must climb inside to find out who stole its golden gears.',
    bands: EMOWG, styles: ['european'], icon: 'clock' },
  { id: 'treehouse', product: C, chip: 'The not-too-scary treehouse', title: 'The Not-Too-Scary Treehouse',
    idea: "Something goes bump in the treehouse every night. It's lonely, not scary, and it needs {names} to be its friends.",
    bands: ['T', 'E', 'M'], styles: ['kodomo', 'shojo'], icon: 'tent' },
  { id: 'cloud-tea', product: C, chip: 'The cloud who came to tea', title: 'The Cloud Who Came to Tea',
    idea: 'A small, shy cloud floats in through the window, and {names} must cheer it up before it rains on the tea party.',
    bands: ['T', 'E', 'M'], styles: ['kodomo', 'shojo'], icon: 'cloud' },
  { id: 'midnight-bakery', product: C, chip: 'The midnight bakery', title: '{hero} and the Midnight Bakery',
    idea: 'Every night a tiny bakery opens behind the bookshop, and {names} must bake the cake that makes wishes come true.',
    bands: ['E', 'M', 'O', 'W'], styles: ['shojo', 'kodomo'], icon: 'cake' },
  { id: 'present-mixup', product: C, chip: 'The great present mix-up', title: 'The Great Present Mix-Up',
    idea: 'Every present in town has the wrong name on it, and {names} must sort them all out before Christmas morning.',
    bands: EMOWG, styles: ['cartoon', 'chibi'], icon: 'gift', occasion: 'christmas', occasionOnly: true },
];

/** The site's idea rows (5 each; the first 3 show on phones). */
export const HERO_IDEA_IDS: Record<IdeaProduct, readonly string[]> = {
  'picture-book': ['garden-rocket', 'underwater-train', 'ant-size', 'comet-race', 'sofa-fort'],
  comic: ['superpowers', 'volcano-treasure', 'robot-chef', 'moonbase-rescue', 'night-market-ninjas'],
};

const OCCASIONS: readonly IdeaOccasion[] = ['birthday', 'christmas', 'school'];

export function ideaById(id: string | null | undefined, product: IdeaProduct): IdeaCard | undefined {
  if (!id) return undefined;
  return IDEA_CARDS.find((c) => c.id === id && c.product === product);
}

/** A known idea id of that product, or 'surprise'; anything else (free text included) is dropped. */
export function ideaFromParam(raw: string | null | undefined, product: IdeaProduct): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim();
  if (s === SURPRISE_IDEA) return SURPRISE_IDEA;
  return ideaById(s, product)?.id;
}

export function occasionFromParam(raw: string | null | undefined): IdeaOccasion | undefined {
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim() as IdeaOccasion;
  return OCCASIONS.includes(s) ? s : undefined;
}

/** Copy of ageInYears (lib/picture-book/casting.ts); local so this module stays client-safe. */
function ageInYears(age: unknown): number | undefined {
  if (typeof age === 'number') return Number.isFinite(age) && age >= 0 ? age : undefined;
  if (typeof age !== 'string') return undefined;
  const s = age.trim().toLowerCase();
  if (!s) return undefined;
  if (/^(new[\s-]?born|baby|infant)$/.test(s)) return 0;
  const m = /^(\d+(?:\.\d+)?)\s*(y|yr|yrs|year|years|m|mo|mos|month|months|w|wk|wks|week|weeks)?\.?(?:\s*old)?$/.exec(s);
  if (!m) return undefined;
  const n = Number(m[1]);
  const unit = m[2] ?? 'y';
  if (unit.startsWith('w')) return n / 52;
  if (unit.startsWith('m')) return n / 12;
  return n;
}

const BABY_WORD = /\b(baby|newborn|infant)\b/i;

function isBaby(p: IdeaCastPerson): boolean {
  if (p.isMainCharacter) return false;
  const a = ageInYears(p.age);
  if (a !== undefined && a < 2) return true;
  return typeof p.relationship === 'string' && BABY_WORD.test(p.relationship);
}

function isElder(p: IdeaCastPerson): boolean {
  return !p.isMainCharacter && isGrandparentOfCast(p);
}

/** Someone in the photo (not the hero) is a grandparent / a baby under 2. */
export function castHas(need: IdeaNeed, cast: IdeaCastPerson[]): boolean {
  return need === 'grandparent' ? cast.some(isElder) : cast.some(isBaby);
}

function named(cast: IdeaCastPerson[]): IdeaCastPerson[] {
  return cast.filter((p) => typeof p.name === 'string' && p.name.trim() !== '');
}

function heroOf(cast: IdeaCastPerson[]): IdeaCastPerson | undefined {
  const people = named(cast);
  return people.find((p) => p.isMainCharacter) ?? people[0];
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const DEFAULT_HERO = 'Our hero';
const firstWord = (s: string) => s.trim().split(/\s+/)[0] ?? s;

type Fill = { hero: string; names: string; elder: string };

/** Words the "Our hero" stand-in stays capitalised after ("Captain Our hero", "Team Our hero"). */
const KEEP_CAPS_AFTER = /\b(?:Captain|Team)\s+$/;

/**
 * `prose`: the stand-in "Our hero" reads lower-case mid-sentence ("Opening our hero's lunchbox"); never in titles,
 * at a sentence start, or after Captain/Team.
 */
function fillText(text: string, f: Fill, prose: boolean): string {
  return text.replace(/\{(hero|names|elder)\}/g, (_m, key: keyof Fill, offset: number) => {
    const v = f[key];
    if (v !== DEFAULT_HERO || !prose || offset === 0) return v;
    const before = text.slice(0, offset);
    if (/[.!?]\s*$/.test(before) || KEEP_CAPS_AFTER.test(before)) return v;
    return v.toLowerCase();
  });
}

function fills(cast: IdeaCastPerson[], short: boolean): Fill {
  const hero = heroOf(cast);
  const heroName = hero ? hero.name.trim() : DEFAULT_HERO;
  const others = named(cast).filter((p) => p !== hero).map((p) => p.name.trim());
  const elderP = named(cast).find(isElder) ?? named(cast).find((p) => isGrandparentOfCast(p));
  const elderName = elderP ? elderP.name.trim() : 'Grandma';
  const pick = (s: string) => (short ? firstWord(s) : s);
  return {
    hero: hero ? pick(heroName) : DEFAULT_HERO,
    names: hero ? joinNames([pick(heroName), ...others]) : DEFAULT_HERO,
    elder: pick(elderName),
  };
}

/** The card with the family's names filled in; the title stays within the input's 80 characters. */
export function fillIdea(card: IdeaCard, cast: IdeaCastPerson[]): FilledIdea {
  const full = fills(cast, false);
  let title = fillText(card.title, full, false);
  if (title.length > TITLE_MAX) title = fillText(card.title, fills(cast, true), false);
  if (title.length > TITLE_MAX) {
    // Last resort (a very long first name): cut at the last space within the limit, never mid-word
    const cut = title.slice(0, TITLE_MAX + 1).lastIndexOf(' ');
    title = (cut > 0 ? title.slice(0, cut) : title.slice(0, TITLE_MAX)).trim();
  }
  return { id: card.id, title, idea: fillText(card.idea, full, true), icon: card.icon, chip: card.chip };
}

function eligible(card: IdeaCard, cast: IdeaCastPerson[], occasion?: IdeaOccasion): boolean {
  if (card.occasionOnly && card.occasion !== occasion) return false;
  if (card.needs && !castHas(card.needs, cast)) return false;
  return true;
}

/** The eligible ideas for this family, in order (pinned, occasion, gated-present, band/style…), at most IDEAS_MAX. */
export function ideaCardsFor(i: {
  product: IdeaProduct; cast: IdeaCastPerson[]; band: ReaderBand;
  occasion?: IdeaOccasion; subStyle?: MangaSubStyle; pinned?: string;
}): FilledIdea[] {
  const tier = (c: IdeaCard): number => {
    if (i.pinned && c.id === i.pinned) return 0;
    if (i.occasion && c.occasion === i.occasion) return 1;
    if (c.needs) return 2;
    const band = c.bands.includes(i.band);
    if (i.product === 'picture-book') return band ? 3 : 6;
    const style = !!i.subStyle && !!c.styles?.includes(i.subStyle);
    if (band && style) return 3;
    if (style) return 4;
    if (band) return 5;
    return 6;
  };
  return IDEA_CARDS
    .filter((c) => c.product === i.product && eligible(c, i.cast, i.occasion))
    .map((c, idx) => ({ c, idx, t: tier(c) }))
    .sort((a, b) => a.t - b.t || a.idx - b.idx)
    .slice(0, IDEAS_MAX)
    .map(({ c }) => fillIdea(c, i.cast));
}

/** The site row's ids: the occasion's ideas first, then the hero ids, deduped, 5. */
export function rowIdeaIds(product: IdeaProduct, occasion?: IdeaOccasion): string[] {
  const occ = occasion ? IDEA_CARDS.filter((c) => c.product === product && c.occasion === occasion).map((c) => c.id) : [];
  return [...new Set([...occ, ...HERO_IDEA_IDS[product]])].slice(0, 5);
}

export function ideaHref(product: IdeaProduct, id: string, occasion?: IdeaOccasion): string {
  return product === 'comic' ? createComicHref({ idea: id, occasion }) : createBookHref({ idea: id, occasion });
}

/**
 * What the step should show when it opens (or the people change): a fresh card when the title and idea are empty
 * (the pinned one, a random one for 'surprise', else the first), the same card re-filled when its text is unedited,
 * and null when the parent wrote their own text (never overwritten). An unedited pick whose card is no longer in
 * `cards` (the family changed and it dropped out) is our text with stale names, so it is replaced by a fresh card,
 * never kept. Callers should pin the current pick (`ideaCardsFor({ pinned })`) so an eligible pick stays listed.
 */
export function initialIdeaPick(i: {
  title: string; idea: string; pick: IdeaPick | null; cards: FilledIdea[]; pinned?: string; random?: () => number;
}): IdeaPick | null {
  const toPick = (c: FilledIdea | undefined): IdeaPick | null => (c ? { id: c.id, title: c.title, idea: c.idea } : null);
  if (i.pick) {
    if (i.title !== i.pick.title || i.idea !== i.pick.idea) return null;
    const card = i.cards.find((c) => c.id === i.pick!.id);
    if (card) return toPick(card);
  } else if (i.title.trim() || i.idea.trim()) {
    return null;
  }
  if (!i.cards.length) return null;
  if (i.pinned === SURPRISE_IDEA) {
    const r = (i.random ?? Math.random)();
    return toPick(i.cards[Math.min(i.cards.length - 1, Math.floor(r * i.cards.length))]);
  }
  return toPick(i.cards.find((c) => c.id === i.pinned) ?? i.cards[0]);
}

/** `idea-card:<id>` while the title or the idea is still the card's text, else undefined (analytics source). */
export function ideaSourceFor(pick: IdeaPick | null, final: { title: string; idea: string }): string | undefined {
  if (!pick) return undefined;
  return final.title === pick.title || final.idea === pick.idea ? `idea-card:${pick.id}` : undefined;
}

/** suggestion_kept / suggestion_edited per field, tagged with the card that filled it. */
export function ideaCardEvents(
  pick: IdeaPick | null,
  final: { title: string; idea: string },
): Array<{ name: 'suggestion_kept' | 'suggestion_edited'; field: 'title' | 'idea'; source: string }> {
  if (!pick) return [];
  const source = `idea-card:${pick.id}`;
  return (['title', 'idea'] as const).map((field) => ({
    name: final[field] === pick[field] ? ('suggestion_kept' as const) : ('suggestion_edited' as const),
    field,
    source,
  }));
}
